import {Component, HostListener, OnInit} from '@angular/core';
import {TranslocoService} from '@ngneat/transloco';
import {FileData, FileErrors} from "../../../model/import-csv/file-data";
import {CsvDeviceData} from "../../../model/import-csv/csv-device-data";
import {DevicePersistRequestCsv} from "../../../model/import-csv/device-persist-request-csv";
import {CsvErrorList} from "../../../model/import-csv/csv-error";
import {DeviceCsvErrors} from "./device-csv-errors";
import {AppConfig} from "../../../shared/global-variables";
import {DevicePropertiesErrors} from "./device-properties-errors";
import {CommonMessagesService} from "../../../services/common-messages.service";
import {DevicesService} from "../../../services/devices.service";
import {Tenant} from "../../../model/tenant";
import {Organization} from "../../../model/organization";
import {TenantsService} from "../../../services/tenants.service";
import {AuthGuard} from "../../../common/auth-guard.service";
import {LoggedUser} from "../../../common/logged-user.service";
import {FormBuilder, FormGroup, Validators} from "@angular/forms";

@Component({
  selector: 'app-devices-csv',
  templateUrl: './devices-csv.component.html',
  styleUrls: ['./devices-csv.component.scss']
})
export class DevicesCsvComponent implements OnInit {

  public devicePropertiesErros: DevicePropertiesErrors = new DevicePropertiesErrors(this.i18n);

  public fileFormatIncorrect: boolean = false;
  public infoCollapsed: boolean = true;
  public files: FileData[] = [];
  public filesWithoutErrors: File[] = [];
  public devices: CsvDeviceData[] = [];

  public loadedData: boolean = false;
  public filesLoaded: boolean = false;
  public filesUploaded: boolean = false;

  public uploadingDevices: boolean = false;

  public rows = 10;
  public first = 0;
  public totalDevices: number;

  tenantSelected: boolean;
  organizationSelected: boolean;
  tenants: Tenant[];
  organizations: Organization[];
  tenant: Tenant;
  organization: Organization;
  csvDeviceForm: FormGroup;

  constructor(
    private readonly commonMessageService: CommonMessagesService,
    private readonly devicesService: DevicesService,
    private readonly i18n: TranslocoService,
    private tenantsService: TenantsService,
    private authGuard: AuthGuard,
    private loggedUser: LoggedUser,
    private formBuilder: FormBuilder
  ) {
  }

  ngOnInit(): void {
    this.loadData();
  }

  private loadData(): void {
    this.loadedData = true;

    const tenant = this.tenant ? {name: this.tenant.name, uuid: this.tenant.uuid} : null;
    const organization = this.organization ? {name: this.organization.name, uuid: this.organization.uuid} : null;
    this.csvDeviceForm = this.formBuilder.group(
      {
        tenant: [tenant, Validators.required],
        organization: [{value: organization, disabled: true}, Validators.required]
      }
    );
  }

  public async processFiles(files: File[]): Promise<void> {
    const filePromises: Promise<FileData>[] = files.map((file, index) => {
      const fileIdentifier: string = 'id-' + index + '-' + (new Date()).getTime();
      return this.readFile(fileIdentifier, file)
    });

    const _files: FileData[] = await Promise.all(filePromises);

    this.files = this.files.concat(_files);
    this.filesWithoutErrors = this.files
      .filter(file => file.fileErrors == null)
      .map(file => file.file);
    this.filesLoaded = true;
    this.filesUploaded = false;
    this.infoCollapsed = true;

    if (this.authGuard.superAdminRequired()) {
      this.tenantsService.getTenantSummaries().toPromise().then(response => {
        this.tenants = response.tenants;
      })
    } else {
      this.tenants = this.loggedUser.userTenants;
      this.tenant = this.tenants[0];
      this.csvDeviceForm.get('tenant').setValue(this.tenant);
      this.csvDeviceForm.get('tenant').disable();
      this.loadOrganizations(this.tenant.uuid).then(() => {
        this.enableTenantDependentElements();
      });
    }
  }

  private loadOrganizations(tenantUuid: string): Promise<any> {
    this.disableTenantDependentElements();
    return Promise.all([
      this.tenantsService.getTenantOrganizationSummaries(tenantUuid).toPromise(),
    ]).then(response => {
      this.organizations = response[0].organizations;
    });
  }

  private enableTenantDependentElements(): void {
    this.tenantSelected = true;
    this.csvDeviceForm.get('organization').enable();
  }

  private disableTenantDependentElements(): void {
    this.tenantSelected = false;
    this.csvDeviceForm.get('organization').disable();
  }

  onTenantChange(event): void {
    if (event.value) {
      this.tenantSelected = true;
      this.organizationSelected = false;
      this.csvDeviceForm.get('organization').setValue(null);
      this.loadOrganizations(event.value.uuid).then(() => {
        this.enableTenantDependentElements();
      });
    }
  }

  public onOrganizationChange(event): void {
    if (event?.value) {
      this.organizationSelected = true;
    }
  }

  public onClearOrganization() {
    this.csvDeviceForm.get('organization').setValue(undefined);
  }

  private readFile(fileIdentifier: string, file: File): Promise<FileData> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = async () => {
        try {
          const fileReader = reader.result.toString().split('\n');
          const fileErrors = this.processDeviceFiles(fileIdentifier, fileReader);
          const fileData: FileData = {
            fileIdentifier,
            file,
            fileErrors
          }
          resolve(fileData);
        } catch (err) {
          reject(err);
        }
      };
      reader.readAsText(file, 'ISO-8859-3');
      reader.onerror = (error) => {
        reject(error)
      };
    });
  }

  public deleteAttachment(index: number): void {
    const fileIdentifier = this.files[index]['fileIdentifier'];
    this.files.splice(index, 1);
    if (this.files.length == 0) {
      this.filesLoaded = false;
      this.infoCollapsed = false;
    }
    this.devices = this.devices.filter(device => device.fileIdentifier != fileIdentifier);
    this.totalDevices = this.devices.length;
    this.filesWithoutErrors = this.files
      .filter(file => file.fileErrors == null)
      .map(file => file.file);
  }

  private processDeviceFiles(fileIdentifier: string, file: string[]): FileErrors | null {
    let devices: CsvDeviceData[] = [];
    let fileErrors: FileErrors = {
      lines: [],
    };

    file.forEach((line, lineIndex) => {
      if (line.trim() !== '' && !line.includes('Serial number')) {
        const rowData = line.split(';');
        if (this.validateNumberOfParams(rowData)) {
          const device = this.getDevicePersistRequestCsv(rowData);
          let csvDeviceData: CsvDeviceData = {
            device,
            fileIdentifier,
            status: 'NONE'
          }

          const errors = this.validateDeviceData(csvDeviceData);
          if (errors != null) {
            csvDeviceData.status = 'ERROR';
            csvDeviceData.errors = errors;
            fileErrors.lines.push(lineIndex - 1);

          } else {
            csvDeviceData.status = 'CORRECT';
          }

          devices.push(csvDeviceData);
        } else {
          fileErrors.lines.push(lineIndex - 1);
        }
      }
    });
    this.fileFormatIncorrect = false;

    if (devices.length > 0) {
      this.devices = this.devices.concat(devices);
    }
    this.totalDevices = this.devices.length;
    return fileErrors.lines.length > 0 ? fileErrors : null;
  }

  private validateNumberOfParams(rowData: string[]): boolean {
    return rowData.length >= 3;
  }

  private getDevicePersistRequestCsv(rowData: string[]): DevicePersistRequestCsv {
    const [serialNumber, deviceAlias, deviceDescription, deviceCategory, deviceTimeZone, deviceTechnicalDetails, deviceTemplate,
      assetAlias, assetDescription, latitude, longitude, assetTimeZone, representation,
      assetTechnicalDetails, assetTemplate, integrationIdentifier] = rowData;

    return {
      serialNumber,
      deviceAlias,
      deviceDescription,
      deviceCategory,
      deviceTimeZone,
      deviceTechnicalDetails,
      deviceTemplate,
      assetAlias,
      assetDescription,
      latitude,
      longitude,
      assetTimeZone,
      representation,
      assetTechnicalDetails,
      assetTemplate,
      integrationIdentifier
    }
  }

  private validateDeviceData(csvDeviceData: CsvDeviceData): CsvErrorList | null {
    const device = csvDeviceData.device;
    const csvDeviceErrors = new DeviceCsvErrors(this.i18n);

    let present = csvDeviceErrors.required(device, 'serialNumber');
    if (present) {
      csvDeviceErrors.noSpecialCharacters(device, 'serialNumber');
      csvDeviceErrors.noWhitespaces(device, 'serialNumber');
      csvDeviceErrors.minLength(device, 'serialNumber', AppConfig.FORM_IDENTIFIER_MIN_LENGTH);
      csvDeviceErrors.maxLength(device, 'serialNumber', 255);
    }

    present = csvDeviceErrors.required(device, 'deviceCategory');
    if (present) {
      csvDeviceErrors.maxLength(device, 'deviceCategory', 255);
    }

    if (device.deviceAlias != null && device.deviceAlias.trim().length > 0) {
      csvDeviceErrors.maxLength(device, 'deviceAlias', 255);
    }

    if (device.deviceDescription != null && device.deviceDescription.trim().length > 0) {
      csvDeviceErrors.maxLength(device, 'deviceDescription', 255);
    }

    if (device.deviceTimeZone != null && device.deviceTimeZone.trim().length > 0) {
      csvDeviceErrors.maxLength(device, 'deviceTimeZone', 255);
      csvDeviceErrors.validTimeZone(device, 'deviceTimeZone');
    }

    if (device.deviceTechnicalDetails != null && device.deviceTechnicalDetails.trim().length > 0) {
      csvDeviceErrors.validJson(device, 'deviceTechnicalDetails');
    }

    if (device.deviceTemplate != null && device.deviceTemplate.trim().length > 0) {
      csvDeviceErrors.maxLength(device, 'deviceTemplate', 255);
    }

    present = csvDeviceErrors.required(device, 'assetAlias');
    if (present) {
      csvDeviceErrors.noSpecialCharacters(device, 'assetAlias');
      csvDeviceErrors.maxLength(device, 'assetAlias', 255);
    }

    if (device.assetDescription != null && device.assetDescription.trim().length > 0) {
      csvDeviceErrors.maxLength(device, 'assetDescription', 255);
    }

    let isNumber = false;
    if (device.latitude != null && device.latitude.trim().length > 0) {
      isNumber = csvDeviceErrors.isFloatNumber(device, 'latitude');
      if (isNumber) {
        csvDeviceErrors.validLatitude(device);
        present = csvDeviceErrors.required(device, 'longitude');
        if (present) {
          isNumber = csvDeviceErrors.isFloatNumber(device, 'longitude');
          if (isNumber) {
            csvDeviceErrors.validLongitude(device);
          }
        }
      }
    }

    if (device.assetTimeZone != null && device.assetTimeZone.trim().length > 0) {
      csvDeviceErrors.maxLength(device, 'assetTimeZone', 255);
      csvDeviceErrors.validTimeZone(device, 'assetTimeZone');
    }

    if (device.assetTechnicalDetails != null && device.assetTechnicalDetails.trim().length > 0) {
      csvDeviceErrors.validJson(device, 'assetTechnicalDetails');
    }

    if (device.representation != null && device.representation.trim().length > 0) {
      csvDeviceErrors.maxLength(device, 'representation', 255);
    }

    if (device.assetTemplate != null && device.assetTemplate.trim().length > 0) {
      csvDeviceErrors.maxLength(device, 'assetTemplate', 255);
    }

    if (device.integrationIdentifier != null && device.integrationIdentifier.trim().length > 0) {
      csvDeviceErrors.maxLength(device, 'integrationIdentifier', 255);
    }

    return Object.entries(csvDeviceErrors.errors).length > 0 ? csvDeviceErrors.errors : null;
  }

  confirmImport(): void {
    if (!this.csvDeviceForm.valid) {
      return;
    }
    if (this.files.length > this.filesWithoutErrors.length) {
      this.commonMessageService.showImportCsvConfirmation(() => this.importCsvFiles());
    } else {
      this.importCsvFiles();
    }
  }

  public importCsvFiles(): void {
    const formValue = this.csvDeviceForm.getRawValue();
    this.devicesService
      .importDevicesFromCsv(this.filesWithoutErrors,
        formValue.tenant.uuid,
        formValue.organization.uuid)
      .subscribe();
    this.commonMessageService.showSuccessfullyUploadedMessage();
    this.filesUploaded = true;
    this.files = [];
    this.filesWithoutErrors = [];
    this.devices = [];
  }

  @HostListener('window:beforeunload')
  canDeactivate(): boolean {
    return !this.files || this.files.length === 0;
  }
}
